import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2, Search } from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';
import { Alert, AlertDescription } from '@/components/ui/alert';

export default function GuestOrderLookup() {
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const [orderNumber, setOrderNumber] = useState('');
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [loadingOrderNumber, setLoadingOrderNumber] = useState(false);

  useEffect(() => {
    // Check for pending order lookup from redirect
    const pendingOrderId = sessionStorage.getItem('pendingOrderLookup');
    if (pendingOrderId) {
      // Check if it's a UUID or order number
      if (pendingOrderId.startsWith('ORD-')) {
        setOrderNumber(pendingOrderId);
      } else {
        // It's a UUID, try to fetch the order number
        setLoadingOrderNumber(true);
        supabase
          .from('orders')
          .select('order_number')
          .eq('id', pendingOrderId)
          .single()
          .then(({ data, error }) => {
            if (data?.order_number) {
              setOrderNumber(data.order_number);
            } else {
              toast({
                title: 'Order ID Detected',
                description: 'Please enter your order number and email to continue',
              });
            }
          })
          .finally(() => setLoadingOrderNumber(false));
      }
      sessionStorage.removeItem('pendingOrderLookup');
    }

    // Check for order ID in location state
    const stateOrderId = location.state?.orderId;
    if (stateOrderId) {
      if (stateOrderId.startsWith('ORD-')) {
        setOrderNumber(stateOrderId);
      } else {
        // It's a UUID, try to fetch the order number
        setLoadingOrderNumber(true);
        supabase
          .from('orders')
          .select('order_number')
          .eq('id', stateOrderId)
          .single()
          .then(({ data, error }) => {
            if (data?.order_number) {
              setOrderNumber(data.order_number);
            }
          })
          .finally(() => setLoadingOrderNumber(false));
      }
    }
  }, [location, toast]);

  const handleLookup = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!orderNumber || !email) {
      toast({
        title: 'Missing Information',
        description: 'Please enter both order number and email address',
        variant: 'destructive',
      });
      return;
    }

    setIsLoading(true);

    try {
      // First, try to find the order by order number and email
      const { data: order, error } = await supabase
        .from('orders')
        .select('id, order_number, customer_email, status')
        .eq('order_number', orderNumber)
        .eq('customer_email', email)
        .single();

      if (error || !order) {
        // If not found with customer_email, try with buyer_email
        const { data: orderByBuyerEmail, error: buyerEmailError } = await supabase
          .from('orders')
          .select('id, order_number, buyer_email, status')
          .eq('order_number', orderNumber)
          .eq('buyer_email', email)
          .single();

        if (buyerEmailError || !orderByBuyerEmail) {
          toast({
            title: 'Order Not Found',
            description: 'No order found with that order number and email combination',
            variant: 'destructive',
          });
          return;
        }

        // Navigate to order details page
        navigate(`/order/${orderByBuyerEmail.id}`);
      } else {
        // Navigate to order details page
        navigate(`/order/${order.id}`);
      }
    } catch (error) {
      console.error('Error looking up order:', error);
      toast({
        title: 'Error',
        description: 'An error occurred while looking up your order',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-brand-accent pt-24 pb-12">
      <div className="container mx-auto px-4 max-w-lg">
        <Card className="bg-white shadow-lg border-0">
          <CardHeader>
            <CardTitle className="text-2xl text-center">Track Your Order</CardTitle>
            <p className="text-center text-gray-600 mt-2">
              Enter your order number and email to view order details
            </p>
          </CardHeader>
          <CardContent>
            {location.state?.message && (
              <Alert className="mb-4 border-brand-primary/20 bg-[#FAF7F0]">
                <AlertDescription>{location.state.message}</AlertDescription>
              </Alert>
            )}
            <form onSubmit={handleLookup} className="space-y-4">
              <div>
                <Label htmlFor="orderNumber">Order Number</Label>
                <Input
                  id="orderNumber"
                  type="text"
                  placeholder="e.g., ORD-25-001234"
                  value={orderNumber}
                  onChange={(e) => setOrderNumber(e.target.value)}
                  className="mt-1"
                  required
                  disabled={loadingOrderNumber}
                />
                {loadingOrderNumber && (
                  <p className="text-sm text-gray-500 mt-1">Loading order number...</p>
                )}
              </div>
              <div>
                <Label htmlFor="email">Email Address</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="Enter the email used for your order"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="mt-1"
                  required
                />
              </div>
              <Button
                type="submit"
                className="w-full bg-[#967F81] hover:bg-[#843329]"
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Looking up...
                  </>
                ) : (
                  <>
                    <Search className="mr-2 h-4 w-4" />
                    Track Order
                  </>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
} 